import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", location_id: "", active: true };

export default function Kassen() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const qc = useQueryClient();

  const terminals = useQuery({
    queryKey: ["terminals-admin"],
    queryFn: async () => (await api.get("/pos/terminals")).data,
  });
  const shops = useQuery({
    queryKey: ["locations", "all-active"],
    queryFn: async () => (await api.get("/locations", { params: { active: true } })).data,
  });
  const shopById = useMemo(
    () => Object.fromEntries((shops.data || []).map((s) => [s.id, s])),
    [shops.data],
  );
  // Shops zuerst, dann übrige aktive Lagerorte (Lager/Automat) — sortiert
  // damit der Admin die typische Auswahl schnell findet.
  const sortedLocations = useMemo(() => {
    const ranked = [...(shops.data || [])].filter((l) => !l.deleted_at);
    ranked.sort((a, b) => {
      const pa = a.type === "shop" ? 0 : 1;
      const pb = b.type === "shop" ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return a.name.localeCompare(b.name);
    });
    return ranked;
  }, [shops.data]);
  const selectedLoc = shopById[form.location_id];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["terminals-admin"] });
    qc.invalidateQueries({ queryKey: ["pos-terminals-active"] });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (editingId) {
        return (await api.put(`/pos/terminals/${editingId}`, form)).data;
      }
      return (await api.post("/pos/terminals", { name: form.name, location_id: form.location_id })).data;
    },
    onSuccess: () => {
      toast.success(editingId ? "Kasse aktualisiert" : "Kasse angelegt");
      setOpen(false); setForm(EMPTY); setEditingId(null);
      invalidate();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const toggle = useMutation({
    mutationFn: async (t) => (await api.put(`/pos/terminals/${t.id}`, { active: !t.active })).data,
    onSuccess: (_, t) => { toast.success(t.active ? "Kasse deaktiviert" : "Kasse aktiviert"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/pos/terminals/${id}`)).data,
    onSuccess: (data) => {
      toast.success(data?.archived ? "Kasse archiviert (Historie erhalten)" : "Kasse gelöscht");
      invalidate();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const openNew = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, location_id: t.location_id, active: t.active });
    setOpen(true);
  };
  const confirmDelete = (t) => {
    if (window.confirm(`Kasse "${t.name}" löschen?\n\nBei bereits vorhandenen Verkäufen wird archiviert.`)) {
      del.mutate(t.id);
    }
  };

  return (
    <div data-testid="terminals-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
        <div className="min-w-0 flex items-center gap-3">
          <CreditCard className="text-cyan-400 shrink-0" size={28} />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-cyan-400">Verkauf</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight truncate">Kassen</h1>
          </div>
        </div>
        <Button data-testid="new-terminal-btn" onClick={openNew}
          className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-sm uppercase tracking-wider font-semibold">
          <Plus size={16} className="mr-2" /> Neue Kasse
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">
              {editingId ? "Kasse bearbeiten" : "Neue Kasse"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Name *</Label>
              <Input data-testid="terminal-field-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm w-full" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Zugeordneter Lagerort *</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                <SelectTrigger data-testid="terminal-field-location" className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm">
                  <SelectValue placeholder="Lagerort wählen…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {sortedLocations.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} <span className="text-zinc-500 ml-2">({s.code})</span>
                      {s.type !== "shop" && <span className="text-yellow-500 ml-2 text-xs uppercase">· {s.type}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-zinc-500 mt-1">
                Verkäufe über diese Kasse reduzieren ausschließlich den Bestand des gewählten Lagerortes.
              </div>
              {selectedLoc && selectedLoc.type !== "shop" && (
                <div data-testid="non-shop-warning" className="mt-2 text-xs text-yellow-500 border border-yellow-500/30 bg-yellow-500/5 rounded-sm p-2">
                  Dieser Lagerort ist kein Shop ({selectedLoc.type}). Verkäufe dieser Kasse werden trotzdem direkt von diesem Bestand abgezogen.
                </div>
              )}
            </div>
            {editingId && (
              <div className="text-xs text-zinc-500 border border-zinc-800 rounded-sm p-3">
                Historische Verkäufe bleiben mit dem ursprünglichen Kassennamen dokumentiert (Audit-Snapshot).
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="terminal-save-btn" onClick={() => save.mutate()}
              disabled={!form.name || !form.location_id}
              className="bg-cyan-400 text-zinc-950 hover:bg-cyan-300 rounded-sm">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="terminals-grid">
        {(terminals.data || []).map((t) => {
          const loc = shopById[t.location_id];
          return (
            <div key={t.id} data-testid={`terminal-card-${t.id}`}
              className="border border-zinc-800 bg-zinc-900 rounded-sm p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-cyan-400">Kasse</div>
                  <div className="font-display text-2xl font-bold uppercase tracking-tight mt-1 truncate">{t.name}</div>
                  <div className="text-sm text-zinc-400 mt-1 truncate" data-testid={`terminal-loc-${t.id}`}>
                    {loc ? `${loc.name} · ${loc.code}` : <span className="text-zinc-600">Lagerort unbekannt</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" data-testid={`terminal-edit-${t.id}`} onClick={() => openEdit(t)}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" className="text-rose-400"
                    data-testid={`terminal-delete-${t.id}`} onClick={() => confirmDelete(t)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className={`text-xs uppercase tracking-wider ${t.active ? "text-emerald-400" : "text-zinc-500"}`}>
                  {t.active ? "Aktiv" : "Deaktiviert"}
                </span>
                <Button size="sm" variant="outline" className="border-zinc-700"
                  data-testid={`terminal-toggle-${t.id}`} onClick={() => toggle.mutate(t)}>
                  {t.active ? "Deaktivieren" : "Aktivieren"}
                </Button>
              </div>
            </div>
          );
        })}
        {!terminals.data?.length && (
          <div className="col-span-full border border-dashed border-zinc-800 rounded-sm p-8 text-center text-zinc-500">
            Noch keine Kassen angelegt.
          </div>
        )}
      </div>
    </div>
  );
}
